import { z } from "zod";
import { hasDisallowedPersonNameChars, normalizePersonNameInput } from "@goppy/domain";
// 🔴 TÁI DÙNG `groupRoleSchema` — KHÔNG khai lại enum vai ở đây. Repo đã có tiền lệ "drift 2
// whitelist" (§3.8): hai danh sách cho cùng một luật thì sớm muộn lệch nhau.
import { GROUP_DISPLAY_NAME_INVALID_CHARS_MESSAGE, groupMemberStatusSchema, groupRoleSchema } from "./groups.js";

/**
 * US-IDM.F2-0289-S06 (@UC9514 @UC9515) — CHẾ ĐỘ ÁNH XẠ DANH TÍNH (identity mapping mode).
 *
 * 🔴 MỘT NGUỒN SỰ THẬT cho "1 user = 1 member (Auto)" vs "n user → 1 member (Manual)". Đọc THẬT
 * (config-driven, KHÔNG toggle no-op): resolver 2 tầng group.settings > system_settings, default
 * suy theo Group.type (standard→auto · parent→manual). Consumer đầu tiên: accept-invite rẽ nhánh.
 *
 *  - auto  : nhóm standard — accept invite ⇒ TỰ tạo GroupMember + self-account (như hiện tại).
 *  - manual: nhóm parent   — accept invite ⇒ KHÔNG tự tạo member; trả cờ needsIdentitySelection để
 *            client dẫn user vào màn One-Time Identity Selection (S07 xử tiếp: chọn con / tạo mới).
 */
export const IDENTITY_MAPPING_MODE = {
  AUTO: "auto",
  MANUAL: "manual",
} as const;

export type IdentityMappingMode =
  (typeof IDENTITY_MAPPING_MODE)[keyof typeof IDENTITY_MAPPING_MODE];

export const identityMappingModeSchema = z.enum(["auto", "manual"]);

/** Khoá system_settings + guardrail allowed enum. groupOverridable=true (nhóm được phép đè). */
export const IDENTITY_MAPPING_MODE_KEY = "identity_mapping_mode";
export const IDENTITY_MAPPING_MODE_ALLOWED = {
  enum: [IDENTITY_MAPPING_MODE.AUTO, IDENTITY_MAPPING_MODE.MANUAL],
} as const;

/**
 * Group.type quyết định DEFAULT khi không có override:
 *  - 'standard' → auto (1 user = 1 member)
 *  - 'parent'   → manual (n user → 1 member học sinh)
 */
export const GROUP_TYPE = {
  STANDARD: "standard",
  PARENT: "parent",
} as const;

export type GroupType = (typeof GROUP_TYPE)[keyof typeof GROUP_TYPE];

/**
 * US-IDM.F2-0289-S05 (@UC9513) — TRƯỞNG NHÓM MAP/REMAP user↔member (owner/admin only).
 *
 * 🔴 linkRole (MembershipAccount) — nguồn sự thật danh tính "user X đại diện member Y". 4 giá trị
 * (mirror CHECK DB membership_accounts_linkRole_check):
 *  - self             : member tự đại diện (nhóm auto — do accept-invite/create ghi, KHÔNG map tay).
 *  - primary_contact  : đại diện CHÍNH của member (vd bố/mẹ chính của học sinh). isPrimaryContact=true.
 *  - secondary_contact: đại diện PHỤ (vd phụ huynh thứ hai). isPrimaryContact=false.
 *  - viewer           : quan sát (kế toán/ban đại diện) — isObligationBearing=false, KHÔNG gánh tiền.
 */
export const MEMBERSHIP_LINK_ROLE = {
  SELF: "self",
  PRIMARY_CONTACT: "primary_contact",
  SECONDARY_CONTACT: "secondary_contact",
  VIEWER: "viewer",
} as const;

export type MembershipLinkRole =
  (typeof MEMBERSHIP_LINK_ROLE)[keyof typeof MEMBERSHIP_LINK_ROLE];

export const membershipLinkRoleSchema = z.enum([
  MEMBERSHIP_LINK_ROLE.SELF,
  MEMBERSHIP_LINK_ROLE.PRIMARY_CONTACT,
  MEMBERSHIP_LINK_ROLE.SECONDARY_CONTACT,
  MEMBERSHIP_LINK_ROLE.VIEWER,
]);

/**
 * linkRole trưởng nhóm được phép GÁN khi map tay (KHÔNG 'self' — self dành cho auto-mode
 * accept-invite/create; gán self tay sẽ nhập nhằng ai là "chủ" member). Mặc định primary_contact
 * khi body không nêu (đại diện chính đầu tiên của member).
 */
export const assignableLinkRoleSchema = z.enum([
  MEMBERSHIP_LINK_ROLE.PRIMARY_CONTACT,
  MEMBERSHIP_LINK_ROLE.SECONDARY_CONTACT,
  MEMBERSHIP_LINK_ROLE.VIEWER,
]);

export type AssignableLinkRole = z.infer<typeof assignableLinkRoleSchema>;

/**
 * Body POST /v1/groups/:id/members/:memberId/accounts — map 1 user vào member.
 *  - userId BẮT BUỘC (User.id cần map). `.strict()` chặn over-post (KHÔNG nhận groupMemberId/id — server
 *    quyết member từ path :memberId).
 *  - linkRole tùy chọn (default primary_contact). 2 phụ huynh 1 học sinh = 2 lần gọi (primary + secondary).
 */
export const mapAccountRequestSchema = z
  .object({
    userId: z.string().uuid(),
    linkRole: assignableLinkRoleSchema.optional(),
  })
  .strict();

export type MapAccountRequest = z.infer<typeof mapAccountRequestSchema>;

/**
 * Body POST /v1/groups/:id/members/:memberId/accounts/:userId/move — remap account sang member khác
 * (trưởng nhóm chọn nhầm MemberIdentity). `.strict()` chặn over-post.
 */
export const moveAccountRequestSchema = z
  .object({
    toMemberId: z.string().uuid(),
  })
  .strict();

export type MoveAccountRequest = z.infer<typeof moveAccountRequestSchema>;

/**
 * Response map/move thành công (account view tối giản — KHÔNG PII: chỉ id + role liên kết).
 *  - groupMemberId: member SAU thao tác (member đích khi move).
 *  - userId: user đã map.
 *  - linkRole / isPrimaryContact / isObligationBearing: trạng thái account sau thao tác.
 * `.strict()` chặn rò field thừa ra response.
 */
export const membershipAccountResponseSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    userId: z.string().uuid(),
    linkRole: membershipLinkRoleSchema,
    isPrimaryContact: z.boolean(),
    isObligationBearing: z.boolean(),
  })
  .strict();

export type MembershipAccountResponse = z.infer<
  typeof membershipAccountResponseSchema
>;

/**
 * Response gỡ account (DELETE). `becameGhost=true` khi member KHÔNG còn account nào sau khi gỡ
 * (hoá ghost/unclaimed) — client cảnh báo "member này chưa có người đại diện".
 *
 * US-E24.F3-0868 (QĐ PO 30/09/2026): gỡ người đại diện CUỐI của suất CON đã duyệt (nhóm phụ huynh) nay trả 200 +
 * `becameGhost=true`, và suất về `unclaimed` — mọi khoản giữ nguyên. Client đọc trạng thái suất MỚI bằng cách nạp lại
 * (không suy từ cờ này).
 */
export const unmapAccountResponseSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    userId: z.string().uuid(),
    becameGhost: z.boolean(),
  })
  .strict();

export type UnmapAccountResponse = z.infer<typeof unmapAccountResponseSchema>;

/** Mã lỗi máy-đọc-được (RFC7807 `code`) cho map/remap — client discriminate theo code. */
export const ACCOUNT_MAPPING_CODE = {
  /** (userId, memberId) đã có account — map trùng ⇒ 409 (idempotent guard @@unique). */
  ALREADY_MAPPED: "GROUP_ACCOUNT_ALREADY_MAPPED",
  /** Account (userId trên member gốc) không tồn tại khi DELETE/move ⇒ 404. */
  ACCOUNT_NOT_FOUND: "GROUP_ACCOUNT_NOT_FOUND",
  /** move sang chính member đang giữ account (no-op) ⇒ 409. */
  MOVE_TO_SAME_MEMBER: "GROUP_ACCOUNT_MOVE_TO_SAME_MEMBER",
  /**
   * 🔴 US-E2.F1-0402 — nối/chuyển account vào ghế mang VAI QUẢN TRỊ ⇒ 409.
   *
   * Chốt chặn GỐC của đường leo thang đặc quyền (PoC 0401): `mapAccount` chỉ gác AI GỌI, không gác
   * NỐI VÀO GHẾ NÀO ⇒ một ADMIN nối tài khoản phụ của mình vào ghế `owner` là tự đúc ra một owner,
   * đi vòng qua `setRole` (OWNER-only) lẫn cỗ máy chuyển-quyền/dissent.
   *
   * `mapAccount` sinh ra để gắn phụ huynh vào suất HỌC SINH, mà suất học sinh luôn `role: MEMBER`
   * ⇒ ràng buộc này không cắt mất ca dùng nào.
   */
  SEAT_NOT_MAPPABLE: "GROUP_ACCOUNT_SEAT_NOT_MAPPABLE",
  /**
   * 🔴 US-E2.F1-0402 — chuyển một account `linkRole='self'` sang ghế KHÁC ⇒ 409.
   *
   * `self` nghĩa là "ghế này CHÍNH LÀ tôi". `moveAccount` giữ `linkRole` bit-for-bit, nên chuyển nó
   * sang ghế đứa trẻ là biến người đó thành "chính chủ" của ghế đó — và họ đổi tên mình sẽ GHI ĐÈ
   * TÊN ĐỨA TRẺ (PoC review trên US-E2.F1-0399). Muốn đại diện cho ghế khác thì gắn account mới với
   * vai người liên hệ, không phải bê cái `self` đi.
   */
  CANNOT_MOVE_SELF_LINK: "GROUP_ACCOUNT_CANNOT_MOVE_SELF_LINK",
  /**
   * 🔴 US-E24.F3-0352 HIGH-D(1) (QĐ PO 09/08/2026) — gỡ account CUỐI CÙNG của ghế `active` ⇒ 409.
   * Ghế `active` là ghế ĐANG gánh nghĩa vụ tiền: `recurring-collection.job` lọc `status='active'`
   * nên vẫn sinh `member_dues` mỗi kỳ, trong khi worker thông báo thấy `primaryUserId === null` thì
   * markDispatched ⇒ **0 thông báo** — suất bị tính tiền mà không ai đại diện, trong im lặng.
   * Chặn tại tay người bấm, KHÔNG flip ngược `active→unclaimed` (flip = suất lặng lẽ rời danh sách
   * thu mà không ai quyết = đổi tên chính con bug đang vá).
   *
   * 🔴 ĐẢO MỘT PHẦN — US-E24.F3-0868 (QĐ PO 30/09/2026): suất CON đã duyệt ở nhóm PHỤ HUYNH (không phải ghế người
   * lớn) ⇒ KHÔNG còn nhận mã này — gỡ người cuối trả 200, suất về `unclaimed`, khoản giữ nguyên (từ QĐ 28/09 C11
   * `unclaimed` vẫn trong diện thu nên lý do "rời danh sách thu" hết đúng). Mã này CÒN cho: ghế ĐANG XẾP HÀNG (người
   * đại diện cuối / liên kết của chính người xin) · liên kết `self` của ghế người lớn · nhóm thường · gỡ `viewer`
   * khỏi suất không còn người đại diện. Luật đầy đủ: `MemberAccountMappingService.assertSeatKeepsRepresentative`.
   */
  LAST_REPRESENTATIVE: "GROUP_ACCOUNT_LAST_REPRESENTATIVE",
  /**
   * 🔴 REVIEW VÒNG 3 — M2: P2002 trên `@@unique([groupId, primaryUserId])` (cột CACHE người đại
   * diện chính) KHÁC HẲN P2002 trên `@@unique([groupMemberId, userId])` (map trùng). Gộp hai ca vào
   * cùng mã `ALREADY_MAPPED` là **trả mã lỗi sai** — người dùng đọc "đã liên kết rồi" trong khi sự
   * thật là "người này đang là đại diện chính của một suất khác trong nhóm".
   */
  PRIMARY_USER_TAKEN: "GROUP_ACCOUNT_PRIMARY_USER_TAKEN",
} as const;

export const ACCOUNT_ALREADY_MAPPED_MESSAGE =
  "Người dùng này đã được liên kết với thành viên.";
export const ACCOUNT_NOT_FOUND_MESSAGE =
  "Không tìm thấy liên kết tài khoản cần thao tác.";
export const MOVE_TO_SAME_MEMBER_MESSAGE =
  "Không thể chuyển liên kết về chính thành viên đang giữ.";
/** Câu chữ hướng người dùng sang việc PHẢI làm trước, không chỉ nói cái bị cấm (§7 error model). */
export const ACCOUNT_PRIMARY_USER_TAKEN_MESSAGE =
  "Người này đang là đại diện chính của một suất khác trong nhóm. " +
  "Hãy gắn họ với vai người liên hệ phụ, hoặc gỡ liên kết cũ trước.";
export const MAP_ACCOUNT_SEAT_NOT_MAPPABLE_MESSAGE =
  "Chỉ gắn được người đại diện cho thành viên thường. " +
  "Muốn đổi vai quản trị thì dùng chức năng phân vai, không gắn thêm tài khoản.";
export const ACCOUNT_CANNOT_MOVE_SELF_LINK_MESSAGE =
  "Liên kết này là của chính người đó với suất của họ nên không chuyển sang người khác được. " +
  "Hãy thêm họ làm người liên hệ của suất kia.";
export const ACCOUNT_LAST_REPRESENTATIVE_MESSAGE =
  "Suất này cần ít nhất một người đại diện. Hãy thêm người đại diện mới trước khi gỡ người cuối cùng.";

/* ===========================================================================
 * US-IDM.F2-0289-S07 (@UC9516) — ONE-TIME IDENTITY SELECTION (nhóm parent/manual sau accept).
 *
 * POST /v1/groups/:id/invitations/identity-selection  (token trong BODY — như accept)
 *
 * BỐI CẢNH: nhóm parent chế độ manual — accept invite KHÔNG tự tạo member (S06 trả
 * needsIdentitySelection). Phụ huynh vào màn này CHỌN học sinh mình đại diện, EITHER:
 *  - {memberId}: chọn học sinh admin đã import (GroupMember unclaimed sẵn có).
 *  - {newMember:{displayName, birthDate?}}: con CHƯA có ⇒ tạo GroupMember unclaimed MỚI (sĩ số +1).
 *    `birthDate` TUỲ CHỌN từ 10/09/2026 (US-E4.F2-0758) — xem docblock của schema bên dưới.
 *
 * Cả 2 nhánh ⇒ ensurePublicHandle (S02) + tạo MembershipAccount(primary_contact nếu member CHƯA có
 * phụ huynh nào; else secondary_contact — phụ huynh thứ 2). IDEMPOTENT: join lại KHÔNG tạo trùng
 * member/account (đã liên kết ⇒ trả account cũ). Token-bound + recipient check (chống IDOR) như accept.
 *
 * ⚠️ EITHER/OR: đúng 1 trong 2 field. Cả hai / không field nào ⇒ 400. `.strict()` chống over-post.
 * ========================================================================= */

/** Độ dài tên học sinh khi tạo mới (chặn rỗng + over-post). */
export const IDENTITY_SELECTION_NAME_MAX_LENGTH = 120;

/** Thông điệp lỗi one-time selection (một nguồn sự thật api ↔ client). */
export const IDENTITY_SELECTION_REQUIRE_ONE_MESSAGE =
  "Chọn đúng một: học sinh có sẵn (memberId), tạo học sinh mới (newMember), hoặc không có con trong lớp (adultNoChild).";
export const IDENTITY_SELECTION_MEMBER_NOT_FOUND_MESSAGE =
  "Không tìm thấy học sinh cần chọn trong nhóm.";
export const IDENTITY_SELECTION_NOT_MANUAL_MESSAGE =
  "Nhóm này không dùng chế độ chọn danh tính thủ công.";
/**
 * 🔴 CONFLICT-CODE-NET-LOWS (@UC9516) — 409: cửa chọn danh tính / liệt kê suất bị gọi cho nhóm KHÔNG dùng chế độ chọn thủ công (và không phải
 * nhóm thường nhận suất giữ chỗ qua link mở). Endpoint sai luồng — không `params`. Trước đây 409 chuỗi trần.
 */
export const IDENTITY_SELECTION_NOT_MANUAL_CODE = "IDENTITY_SELECTION_NOT_MANUAL" as const;

/**
 * 🔴 US-AIV2-0111 C11 (@UC0342 @UC0343 @UC0345 · QĐ PO 28/09 tối, phương án A) — XIN NHẬN SUẤT GIỮ CHỖ ở nhóm thường.
 *
 * Mã ỔN ĐỊNH, ngôn-ngữ-trung-tính (client map `code → câu`); `message` chỉ để debug. Mọi mã là FAIL-CLOSED: không đoán,
 * không tự đổi sang suất khác, không tạo ghế thứ hai.
 *  · `PENDING_BY_OTHER` (409) — suất đã có MỘT lời xin khác đang chờ duyệt (người thứ hai bấm cùng suất);
 *  · `TARGET_UNAVAILABLE` (409) — suất không còn chờ người nhận (đã có người, bị xoá…), lúc xin HOẶC lúc duyệt;
 *  · `ALREADY_MEMBER` (409) — người xin đang là thành viên nhóm rồi ⇒ nhận thêm suất là MỘT người HAI ghế (đóng đôi);
 *  · `NEW_SEAT_NOT_ALLOWED` (422) — nhóm thường không tự khai suất mới qua link ("Tôi không có trong danh sách" = `accept`);
 *  · `APPROVE_VIA_QUEUE` (409) — cửa duyệt KHÁC (yêu cầu gia nhập) chạm ghế đang xin nhận suất ⇒ duyệt ở hàng chờ nhóm.
 */
export const SEAT_CLAIM_CODE = {
  PENDING_BY_OTHER: "SEAT_CLAIM_PENDING_BY_OTHER",
  TARGET_UNAVAILABLE: "SEAT_CLAIM_TARGET_UNAVAILABLE",
  ALREADY_MEMBER: "SEAT_CLAIM_ALREADY_MEMBER",
  NEW_SEAT_NOT_ALLOWED: "SEAT_CLAIM_NEW_SEAT_NOT_ALLOWED",
  APPROVE_VIA_QUEUE: "SEAT_CLAIM_APPROVE_VIA_QUEUE",
  /**
   * Review #1016 HIGH-1 — O/A gắn/chuyển người vào suất GIỮ CHỖ của nhóm THƯỜNG bằng màn gắn/chuyển tài khoản ⇒ 409.
   * Suất giữ chỗ nhóm thường chỉ có MỘT đường có chủ: người đó bấm link, chọn tên, O/A duyệt ở hàng chờ.
   */
  USE_CLAIM_FLOW: "SEAT_CLAIM_USE_CLAIM_FLOW",
  /**
   * Review #1016 lượt 2 MEDIUM-1 — hai mã của phía NGƯỜI DUYỆT (O/A). Cùng hoàn cảnh với `ALREADY_MEMBER`/`TARGET_UNAVAILABLE`
   * nhưng người đọc khác: hai mã kia nói với NGƯỜI XIN ("Bạn đã là thành viên…"), đọc lên màn duyệt là nói sai người.
   * Mã riêng ⇒ client map sang câu cho người duyệt kèm lối ra (từ chối lời xin).
   */
  CLAIMANT_ALREADY_SEATED: "SEAT_CLAIM_CLAIMANT_ALREADY_SEATED",
  APPROVE_TARGET_UNAVAILABLE: "SEAT_CLAIM_APPROVE_TARGET_UNAVAILABLE",
} as const;
export const SEAT_CLAIM_PENDING_BY_OTHER_MESSAGE =
  "Suất này đang có người khác xin nhận, chờ trưởng nhóm duyệt.";
export const SEAT_CLAIM_TARGET_UNAVAILABLE_MESSAGE = "Suất này không còn chờ người nhận.";
export const SEAT_CLAIM_ALREADY_MEMBER_MESSAGE = "Bạn đã là thành viên của nhóm này.";
export const SEAT_CLAIM_NEW_SEAT_NOT_ALLOWED_MESSAGE =
  "Nhóm này không tự khai suất mới qua link — chọn tên của bạn trong danh sách, hoặc vào nhóm như người mới.";
export const SEAT_CLAIM_APPROVE_VIA_QUEUE_MESSAGE =
  "Người này đang xin nhận một suất giữ chỗ — hãy duyệt ở danh sách chờ của nhóm.";
export const SEAT_CLAIM_USE_CLAIM_FLOW_MESSAGE =
  "Suất giữ chỗ ở nhóm này chỉ nhận qua link mời — người đó mở link, chọn tên mình, rồi duyệt ở danh sách chờ.";
export const SEAT_CLAIM_CLAIMANT_ALREADY_SEATED_MESSAGE =
  "Người này đã có chỗ trong nhóm — hãy từ chối lời xin nhận chỗ.";
export const SEAT_CLAIM_APPROVE_TARGET_UNAVAILABLE_MESSAGE =
  "Chỗ người này xin nhận không còn trống — hãy từ chối lời xin để họ chọn lại.";

/** Ngày sinh học sinh mới — chuỗi 'YYYY-MM-DD' (DATE, không giờ). Server parse sang Date @db.Date. */
export const identitySelectionBirthDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Ngày sinh phải theo định dạng YYYY-MM-DD.");

/**
 * Nhánh TẠO MỚI học sinh: tên (bắt buộc) + ngày sinh (**TUỲ CHỌN**). `.strict()` chống over-post.
 *
 * 🔴 `birthDate` ĐÃ NỚI THÀNH TUỲ CHỌN — QĐ người dùng 10/09/2026 (US-E4.F2-0758). Ghi lại lý do vì
 * dòng cũ khai "bắt buộc" kèm một lập luận nghe rất thuyết phục, và người sau sẽ muốn siết lại:
 *
 *  · Bất đối xứng đã tồn tại: `createGroupMemberSeatSchema` (`groups.ts:611`) — admin nhập cả lớp —
 *    để `birthDate` **optional** từ đầu. Cùng một việc "khai một suất học sinh", hai đường, hai
 *    luật. Cột `group_members.birthDate` cũng `nullable` (`schema.prisma:625`). Bên siết là đường
 *    NÀY, và nó siết đúng người ít có thông tin nhất: phụ huynh đang đứng giữa luồng nhận lời mời.
 *  · Lập luận cũ *"hai bé TRÙNG TÊN trong một lớp"* KHÔNG mất hiệu lực, nhưng nó nói về việc **CHỌN
 *    một suất có sẵn** (ở đó ngày sinh do nhà trường nhập và hiển thị để phụ huynh tự soi) — chứ
 *    không phải việc TẠO một suất mới, nơi chưa có gì để nhầm với gì.
 *
 * ⚠️ ĐÁNH ĐỔI ĐÃ CHẤP NHẬN: suất tạo qua đường này có thể không có ngày sinh, nên phụ huynh THỨ HAI
 * vào sau sẽ thấy một dòng trùng tên không có ngày để phân biệt. Đó là cái giá của việc không chặn
 * người dùng ở bước cuối; bù lại admin vẫn điền được ngày sinh sau qua màn quản lý thành viên.
 *
 * 🔴 TUỲ CHỌN = **VẮNG KHOÁ**, không phải `null`. `.optional()` nhận `undefined` chứ không nhận
 * `null`, nên client dựng body kiểu `{birthDate: valueOrNull}` sẽ ăn 400 ở đúng ca người dùng bỏ
 * trống ô. Có test khoá cả hai vế ở `identity-selection.test.ts`.
 */
export const identitySelectionNewMemberSchema = z
  .object({
    /**
     * 🔴 NAME-SANITIZE-REMAINING-DOORS (QĐ PO 03/10, @UC9516 @UC0055) — tên suất do người nhận LINK MỜI gõ tay: cửa quyền THẤP NHẤT (chưa là
     * thành viên, chỉ cầm link) ghi một cái tên mà CẢ nhóm đọc (danh sách, bảng thu, câu trợ lý). Cùng chuẩn hoá + chốt ký tự với tên suất
     * admin tạo (`createGroupMemberSeatSchema`): "Phong. Thủ quỹ: Phong" ⇒ 400 `{ field: "newMember.displayName", reason: "format" }`, 0 ghi;
     * chuỗi KIỂM = chuỗi LƯU (`normalizePersonNameInput` chạy trước). Thứ tự lỗi giữ: rỗng → quá dài → ký tự.
     */
    displayName: z.preprocess(
      normalizePersonNameInput,
      z
        .string()
        .trim()
        .min(1, "Tên học sinh không được để trống.")
        .max(IDENTITY_SELECTION_NAME_MAX_LENGTH, "Tên học sinh quá dài.")
        .refine((name) => !hasDisallowedPersonNameChars(name), GROUP_DISPLAY_NAME_INVALID_CHARS_MESSAGE),
    ),
    birthDate: identitySelectionBirthDateSchema.optional(),
  })
  .strict();

export type IdentitySelectionNewMember = z.infer<
  typeof identitySelectionNewMemberSchema
>;

/* ===========================================================================
 * QĐ người dùng 27/10/2026 (@UC9516) — CHỌN NHIỀU SUẤT TRONG MỘT LƯỢT: `POST /v1/groups/:id/invitations/identity-selection/batch`.
 * Xem `seat-representative-requests.ts` cho phía O/A (lời xin làm người đại diện khi suất đã có người).
 * ========================================================================= */

/** Trần số suất trong MỘT lượt chọn (có sẵn + mới). Một phụ huynh hiếm khi có quá vài con trong một lớp. */
export const IDENTITY_SELECTION_BATCH_MAX = 20;

/** Mã lỗi ổn định (client map `code → câu`). */
export const IDENTITY_SELECTION_BATCH_CODE = {
  /**
   * Lượt chọn có HAI suất trở lên thuộc loại "đã từng vào nhóm rồi bị trả về trống" (người đại diện cuối rời/bị gỡ). Loại suất này đi
   * đường xin nhận qua GHẾ CHỜ, và mỗi người chỉ có MỘT ghế chờ trỏ MỘT suất ⇒ không gộp được hai suất như vậy vào một lượt. 422,
   * 0 ghi — lối ra: chọn từng suất một.
   */
  RELEASED_SEAT_MULTI: "IDENTITY_SELECTION_RELEASED_SEAT_MULTI",
} as const;
export const IDENTITY_SELECTION_RELEASED_SEAT_MULTI_MESSAGE =
  "Mỗi lượt chỉ xin nhận lại được một suất đã trống. Hãy chọn từng suất một.";
export const IDENTITY_SELECTION_BATCH_EMPTY_MESSAGE = "Hãy chọn ít nhất một suất.";
export const IDENTITY_SELECTION_BATCH_TOO_MANY_MESSAGE = `Mỗi lượt chọn tối đa ${IDENTITY_SELECTION_BATCH_MAX} suất.`;
export const IDENTITY_SELECTION_BATCH_DUPLICATE_MESSAGE = "Một suất bị chọn hai lần.";

/**
 * Body batch. `.strict()`: khoá lạ ⇒ 400 ở biên. Tổng `memberIds` + `newMembers` trong [1, IDENTITY_SELECTION_BATCH_MAX], `memberIds`
 * không trùng. Không có nhánh "người lớn không có con" (đã gỡ khỏi app 27/10 — endpoint đơn vẫn nhận cho app cũ).
 */
export const identitySelectionBatchRequestSchema = z
  .object({
    token: z.string().trim().min(1, "Thiếu token lời mời.").max(256, "Token lời mời không hợp lệ."),
    memberIds: z.array(z.string().uuid()).max(IDENTITY_SELECTION_BATCH_MAX).optional(),
    newMembers: z.array(identitySelectionNewMemberSchema).max(IDENTITY_SELECTION_BATCH_MAX).optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    const ids = val.memberIds ?? [];
    const total = ids.length + (val.newMembers?.length ?? 0);
    if (total === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, message: IDENTITY_SELECTION_BATCH_EMPTY_MESSAGE });
    if (total > IDENTITY_SELECTION_BATCH_MAX) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: IDENTITY_SELECTION_BATCH_TOO_MANY_MESSAGE });
    }
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["memberIds"], message: IDENTITY_SELECTION_BATCH_DUPLICATE_MESSAGE });
    }
  });

export type IdentitySelectionBatchRequest = z.infer<typeof identitySelectionBatchRequestSchema>;

/**
 * Kết quả TỪNG suất — ba sự thật khác nhau, client phải nói đúng từng cái:
 *  · `joined`                   — đã là người đại diện của suất, suất đang `active` (kênh email đích danh: người mời đã duyệt trước);
 *  · `pending_approval`         — đã gắn vào suất nhưng SUẤT còn chờ O/A duyệt vào nhóm (`join_pending`) — link mở, suất chưa ai nhận;
 *  · `representative_requested` — suất đã có người đại diện ⇒ chỉ ghi LỜI XIN, CHƯA có liên kết nào, chờ O/A duyệt.
 */
export const IDENTITY_SELECTION_SEAT_OUTCOME = {
  JOINED: "joined",
  PENDING_APPROVAL: "pending_approval",
  REPRESENTATIVE_REQUESTED: "representative_requested",
} as const;
export const identitySelectionSeatOutcomeSchema = z.enum([
  IDENTITY_SELECTION_SEAT_OUTCOME.JOINED,
  IDENTITY_SELECTION_SEAT_OUTCOME.PENDING_APPROVAL,
  IDENTITY_SELECTION_SEAT_OUTCOME.REPRESENTATIVE_REQUESTED,
]);
export type IdentitySelectionSeatOutcome = z.infer<typeof identitySelectionSeatOutcomeSchema>;

export const identitySelectionBatchSeatResultSchema = z
  .object({
    /** Suất (`group_members.id`) — suất có sẵn được chọn, hoặc suất VỪA tạo. */
    memberId: z.string().uuid(),
    createdMember: z.boolean(),
    outcome: identitySelectionSeatOutcomeSchema,
  })
  .strict();

/** Kết quả theo ĐÚNG thứ tự gửi: `memberIds` trước, rồi `newMembers`. */
export const identitySelectionBatchResponseSchema = z
  .object({
    publicHandle: z.string(),
    seats: z.array(identitySelectionBatchSeatResultSchema),
  })
  .strict();

export type IdentitySelectionBatchResponse = z.infer<typeof identitySelectionBatchResponseSchema>;

/**
 * Body POST identity-selection — EITHER {memberId} OR {newMember}. token trong body (như accept, để
 * token thô KHÔNG lọt access-log). `.strict()` + `.superRefine` ép ĐÚNG 1 nhánh (chống mơ hồ):
 *  - cả memberId lẫn newMember ⇒ 400.
 *  - không field nào (chỉ token) ⇒ 400.
 */
export const identitySelectionRequestSchema = z
  .object({
    token: z
      .string()
      .trim()
      .min(1, "Thiếu token lời mời.")
      .max(256, "Token lời mời không hợp lệ."),
    memberId: z.string().uuid().optional(),
    newMember: identitySelectionNewMemberSchema.optional(),
    /**
     * 🔴 US-AIV2-0112 H10 (@UC0349 · QĐ PO Q4 = KHÔNG 28/09 + QĐ 29/09) — *"Tôi không có con trong lớp"* (giáo viên, ban đại
     * diện). Server dựng ghế NGƯỜI LỚN (`linkRole = self`) CHỜ O/A duyệt — ghế này KHÔNG BAO GIỜ gánh nghĩa vụ ở nhóm phụ huynh
     * (`buildObligationMemberWhere`). `z.literal(true)`: `false` không phải một lựa chọn, nó là "chưa chọn" ⇒ 400, không đoán.
     * Chỉ nhóm phụ huynh; nhóm khác ⇒ 422 `SEAT_CLAIM_NEW_SEAT_NOT_ALLOWED`.
     */
    adultNoChild: z.literal(true).optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    const chosen = [val.memberId !== undefined, val.newMember !== undefined, val.adultNoChild !== undefined].filter(Boolean).length;
    // ĐÚNG 1 nhánh trong 3. Hai nhánh HOẶC không nhánh nào ⇒ lỗi (client phải chọn rõ ràng).
    if (chosen !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: IDENTITY_SELECTION_REQUIRE_ONE_MESSAGE,
      });
    }
  });

export type IdentitySelectionRequest = z.infer<
  typeof identitySelectionRequestSchema
>;

/**
 * Response identity-selection (200): member được chọn/tạo + account vừa liên kết. KHÔNG PII người
 * khác — chỉ id + linkRole của account caller.
 *  - groupMemberId: member (học sinh) caller đại diện.
 *  - linkRole: primary_contact (phụ huynh đầu) | secondary_contact (phụ huynh thứ 2+).
 *  - createdMember: true khi TẠO MỚI member (sĩ số +1); false khi chọn member có sẵn.
 *  - publicHandle: handle của caller (ensurePublicHandle — mã tra cứu công khai, KHÔNG bí mật).
 * `.strict()` chặn rò field thừa.
 */
export const identitySelectionResponseSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    linkRole: membershipLinkRoleSchema,
    createdMember: z.boolean(),
    publicHandle: z.string(),
    /**
     * 🔴 US-E4.F2-0580 (review #652 M3) — TRẠNG THÁI SUẤT SAU LƯỢT CHỌN.
     *
     * Kênh thường (`accept-invite`) đã trả `membershipStatus` để client biết dựng màn "đang chờ
     * duyệt" thay vì đẩy người ta vào không gian nhóm. Nhóm PARENT đi qua đường này thì không có
     * tín hiệu nào ⇒ phụ huynh tự khai con qua link lớp xong bị đẩy sang màn nhóm ⇒ `GroupRoleGuard`
     * đòi `status = active` ⇒ **404**, và họ đọc nó thành "link hỏng" (đúng ca §5.1 của task).
     * Cùng một sự thật thì phải nói ở cả hai cửa, nếu không client phải ĐOÁN — và đoán sai ở đây là
     * đoán rằng người ta đã vào nhóm.
     *
     * `.optional()` có chủ đích: đường email/manual cũ không đổi hành vi, và client cũ bỏ qua
     * field mới thì vẫn chạy như trước. Vắng field ⇒ đọc là `active` như hôm nay.
     */
    membershipStatus: groupMemberStatusSchema.optional(),
    /**
     * 🔴 US-AIV2-0111 C11 (@UC0342) — suất GIỮ CHỖ mà lượt này XIN nhận (nhóm thường). Khi có field này thì
     * `groupMemberId` là GHẾ CHỜ của người xin (chưa phải suất đích), `membershipStatus` = `join_pending`, và suất đích
     * CHƯA đổi gì — chỉ đổi lúc O/A duyệt. Vắng ⇒ đường nhóm phụ huynh như cũ.
     */
    claimTargetMemberId: z.string().uuid().optional(),
  })
  .strict();

export type IdentitySelectionResponse = z.infer<
  typeof identitySelectionResponseSchema
>;

/* ===========================================================================
 * US-E24.F8-0357-S01 (@UC9594) — DANH SÁCH HỌC SINH CHỜ NHẬN cho màn D10 "tìm học sinh".
 *
 * POST /v1/groups/:id/invitations/identity-selection/options   (token trong BODY)
 *
 * BỐI CẢNH (audit SCREEN_API_MAP 06/08/2026, lỗ A): `GET /v1/groups/:id/members` chặn HAI lớp với
 * người được mời — `GroupRoleGuard` đòi member ACTIVE (họ chưa là member) ⇒ 404; và filter
 * `status='active'` lọc mất chính các suất `unclaimed` cần chọn. Không endpoint nào liệt kê được
 * ứng viên ⇒ nhánh "chọn con đã có trong danh sách lớp" của D10 là lời hứa UI không có backend.
 *
 * 🔴 POST chứ không GET, và token đi qua BODY: quy ước repo (`invitations.ts:211-213`) — token thô
 * KHÔNG được lọt vào access-log `req.url` / `instance` của ProblemDetail; pino redact sẵn
 * `req.body.token`. Cursor/limit là dữ liệu KHÔNG nhạy cảm ⇒ đi QUERY, để body giữ đúng `{token}`
 * `.strict()` (mọi field lạ ⇒ 400 tại biên, 0 query chạm DB).
 *
 * 🔴 PAYLOAD TỐI THIỂU LÀ RÀNG BUỘC BẢO MẬT. Người gọi là NGƯỜI LẠ VỚI NHÓM (mới cầm link mời) và
 * đây là danh sách TRẺ EM của một lớp. Response CHỈ có `memberId` (để gửi lại ở bước selection),
 * `displayName`, `birthDate`, và cờ `hasGuardian`. CẤM: `userId`/`primaryUserId` · email/phone ·
 * MỌI trường tiền (ranh giới với US-E24.F3-0352) · tên/định danh của phụ huynh khác.
 * `hasGuardian` chỉ nói "suất này đã có người nhận chưa", KHÔNG nói AI nhận.
 *
 * 🔴 `birthDate` TỪNG BỊ CẤM Ở ĐÂY, nay được NỚI CÓ CHỦ Ý (QĐ người dùng 14/08/2026) — ghi lại lý
 * do cả hai chiều để không ai "sửa ngược" mà không biết mình đang đánh đổi cái gì:
 *  · VÌ SAO NỚI: không có ngày sinh thì hai trẻ TRÙNG TÊN trong cùng một lớp là không phân biệt
 *    được, và chọn nhầm suất nghĩa là gánh nghĩa vụ tiền của con nhà khác — hỏng nặng hơn hẳn.
 *  · CÁI ĐÁNH ĐỔI, đã nói rõ và được chấp nhận: endpoint này KHÔNG đòi tư cách thành viên (quyền
 *    do TOKEN quyết), nên bất kỳ ai cầm/được chuyển tiếp link mời đều đọc được ngày sinh đầy đủ
 *    của MỌI trẻ trong lớp. Đây là bề mặt PII trẻ em, không phải một field vô hại.
 *  · Hàng rào CÒN NGUYÊN: `@Throttle` chống brute-force token · `@Audited` ghi vết mọi lượt đọc ·
 *    `.strict()` vẫn chặn mọi field khác. Nới MỘT field, không mở cửa.
 * ========================================================================= */

/** Trần limit 1 trang options (mirror GROUP_MEMBERS_MAX_LIMIT về tinh thần — list lớp không dài). */
export const IDENTITY_SELECTION_OPTIONS_MAX_LIMIT = 50;

/** Cursor opaque luôn ngắn; chặn input khổng lồ (DoS) ngay tại biên trước khi decode. */
export const IDENTITY_SELECTION_OPTIONS_MAX_CURSOR_LENGTH = 512;

/**
 * Một ứng viên trong danh sách chọn.
 *  - memberId    : GroupMember.id — gửi lại nguyên vẹn ở bước `identity-selection`.
 *  - displayName : tên hiển thị của suất; null khi admin import chưa đặt.
 *  - birthDate   : `YYYY-MM-DD` NGÀY TRẦN (không phải mốc thời gian), `null` khi chưa nhập. Cùng
 *                  DẠNG DÂY với `newMember.birthDate` của bước tạo suất — một khái niệm, một định
 *                  dạng. 🔴 Server trả GIÁ TRỊ, client lo ĐỊNH DẠNG hiển thị (`dd/MM/yyyy`): API
 *                  không trả chuỗi đã format, đúng luật i18n "Format ≠ giá trị" (CLAUDE.md §7).
 *  - hasGuardian : ĐÃ có phụ huynh nhận chưa (suy từ `count(MembershipAccount) > 0`) — cờ BOOLEAN,
 *                  cố ý không kèm bất kỳ định danh nào của người đã nhận.
 *
 * 🔴 KHÔNG có `status`: `unclaimed` đang sống trong DB nhưng VẮNG MẶT trong `GROUP_MEMBER_STATUS`
 * (5 giá trị) và không có CHECK constraint nào chặn nó. Phơi `status` ra ở đây là hợp-thức-hoá một
 * trạng thái chưa có trong hợp đồng chung — việc đó thuộc US-E24.F3-0352 (task sở hữu vòng đời
 * `unclaimed`), không phải task bề mặt này.
 */
export const identitySelectionOptionItemSchema = z
  .object({
    memberId: z.string().uuid(),
    displayName: z.string().nullable(),
    // Regex đúng bằng regex của `newMember.birthDate` — hai đầu của CÙNG một khái niệm không được
    // phép lệch định dạng. Gửi ISO-8601 đầy đủ (`...T00:00:00.000Z`) ⇒ reject tại biên.
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable(),
    hasGuardian: z.boolean(),
  })
  .strict();

export type IdentitySelectionOptionItem = z.infer<
  typeof identitySelectionOptionItemSchema
>;

/** Envelope cursor chuẩn repo `{data, pageInfo{hasNext, nextCursor}}`. `.strict()` chống rò field. */
export const identitySelectionOptionsResponseSchema = z
  .object({
    data: z.array(identitySelectionOptionItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type IdentitySelectionOptionsResponse = z.infer<
  typeof identitySelectionOptionsResponseSchema
>;

/** Query phân trang (KHÔNG nhạy cảm ⇒ đi query, khác token). Cursor rác ⇒ 400 ở service. */
export const identitySelectionOptionsQuerySchema = z.object({
  cursor: z
    .string()
    .max(IDENTITY_SELECTION_OPTIONS_MAX_CURSOR_LENGTH)
    .optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(IDENTITY_SELECTION_OPTIONS_MAX_LIMIT)
    .optional(),
  /**
   * QĐ người dùng 27/10/2026 — `all` ⇒ nhóm PHỤ HUYNH trả CẢ suất đã có người đại diện (`hasGuardian: true`) — app mới chọn chúng qua
   * `identity-selection/batch`, nơi suất đã có người ⇒ lời xin chờ O/A duyệt. VẮNG ⇒ như cũ (chỉ suất chờ người nhận): app bản cũ chọn
   * qua endpoint đơn, nơi suất đã có người không đi đường chờ duyệt — mở danh sách cho nó là mở một đường không ai duyệt.
   */
  scope: z.enum(["claimable", "all"]).optional(),
});

export type IdentitySelectionOptionsQuery = z.infer<
  typeof identitySelectionOptionsQuerySchema
>;

/* ===========================================================================
 * US-E24.F8-0357-S03 (@UC9596 @UC9597) — ĐỌC danh sách account của một suất (màn D2).
 *
 * GET /v1/groups/:id/members/:memberId/accounts
 *
 * BỐI CẢNH (audit SCREEN_API_MAP 06/08/2026, lỗ C): surface accounts có ĐỦ đường GHI (POST map ·
 * DELETE unmap · POST move) và **0 đường ĐỌC**. Hệ quả rất cụ thể: `DELETE .../accounts/:userId`
 * và `.../accounts/:userId/move` đều CẦN `userId`, mà không endpoint nào trả `userId` ra ⇒ nút
 * "Gỡ liên kết" / "Chuyển sang suất khác" trên D2 là nút bấm KHÔNG CÓ THAM SỐ.
 *
 * 🔴 `userId` là lý do endpoint này tồn tại ⇒ bắt buộc có. Nhưng PII thì tuyệt đối không:
 * KHÔNG email/phone trong mọi trường hợp.
 *
 * 🔴 ĐIỀU CHỈNH — QĐ người dùng 14/08/2026 (US-E4.F2-0414): slice đầu CỐ Ý không trả tên/avatar với
 * lập luận "hai phụ huynh của cùng một đứa trẻ có thể KHÔNG quen nhau". Nay NỚI cho MỌI member
 * trong nhóm đều thấy `fullName`/`avatarUrl`/`role` — KHÔNG redaction theo vai. Lý do: màn "gắn phụ
 * huynh" là danh sách có tick, mà một danh sách chỉ có `userId` dạng uuid thì người bấm không thể
 * biết mình đang tick ai. Ranh giới GIỮ NGUYÊN: `email`/`phone`/`passwordHash` vẫn KHÔNG BAO GIỜ
 * rời API (khoá bằng AC11 `group-people-listing.int-spec.ts` + AC6 `membership-accounts-read`).
 * ========================================================================= */

/** Trần limit 1 trang (một suất hiếm khi quá 2-3 phụ huynh, nhưng quy ước repo là MỌI list có cursor). */
export const MEMBERSHIP_ACCOUNTS_MAX_LIMIT = 50;

/** Cursor opaque luôn ngắn; chặn input khổng lồ (DoS) trước khi decode. */
export const MEMBERSHIP_ACCOUNTS_MAX_CURSOR_LENGTH = 512;

/**
 * Một liên kết user↔member.
 *  - userId              : dùng NGAY cho DELETE/move (lý do tồn tại của endpoint).
 *  - linkRole            : self | primary_contact | secondary_contact | viewer (mirror CHECK DB).
 *  - isPrimaryContact    : đại diện CHÍNH — client hiện badge.
 *  - isObligationBearing : có gánh nghĩa vụ tiền không (viewer = false).
 *  - createdAt           : thời điểm liên kết (ISO-8601).
 *  - fullName            : tên NGƯỜI đại diện (US-E4.F2-0414 — QĐ người dùng 14/08/2026).
 *                          🔴 US-E4.F2-0846 (22/09/2026): = `membership_accounts.display_name` (tên TRONG NHÓM, phụ huynh tự
 *                          đặt khi vào nhóm — US-E2.F1-0399) **nếu có**, ngược lại `users.fullName`. Trước 0846 server chỉ
 *                          trả `users.fullName` ⇒ tên vừa nhập không bao giờ hiện — "ghi đúng chỗ, đọc sai chỗ".
 *  - avatarUrl           : URL ĐÃ resolve (KHÔNG phải key R2 thô), `null` = chưa có ảnh.
 *  - role                : xem khối doc ngay dưới — `null` là một CÂU TRẢ LỜI, không phải thiếu dữ liệu.
 */
export const membershipAccountListItemSchema = z
  .object({
    userId: z.string().uuid(),
    linkRole: membershipLinkRoleSchema,
    isPrimaryContact: z.boolean(),
    isObligationBearing: z.boolean(),
    createdAt: z.string().datetime(),
    // `null` khi User chưa đặt tên — KHÔNG bịa chuỗi rỗng để UI khỏi phải nghĩ.
    fullName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    /**
     * 🔴 VAI TRONG NHÓM CỦA CHÍNH NGƯỜI NÀY — `null` khi họ KHÔNG có ghế của riêng mình.
     *
     * Ở nhóm `parent` chạy IDENTITY_MODE_MANUAL, phụ huynh KHÔNG có `GroupMember` nào: họ chỉ là
     * `MembershipAccount` gắn trên ghế CỦA CON. Không có ghế ⇒ không có vai ⇒ `null`.
     *
     * ⚠️ TUYỆT ĐỐI KHÔNG lấy `role` của ghế đang xem (ghế đứa con) rồi gán cho phụ huynh. Đó đúng là
     * mẫu *"suy chủ thể từ ngữ cảnh đang bấm"* mà CLAUDE.md §3.6 gọi là bug đắt nhất repo (bản gốc:
     * suy `payerMemberId` từ actor bấm duyệt ⇒ cả nhóm nợ nhầm thủ quỹ). Trả `"member"` cho chắc
     * cũng SAI: nó khẳng định một tư cách mà người đó không có.
     */
    role: groupRoleSchema.nullable(),
  })
  .strict();

export type MembershipAccountListItem = z.infer<
  typeof membershipAccountListItemSchema
>;

/** Envelope cursor chuẩn repo. Suất chưa có phụ huynh ⇒ `data: []` (KHÔNG 404). */
export const membershipAccountsListResponseSchema = z
  .object({
    data: z.array(membershipAccountListItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type MembershipAccountsListResponse = z.infer<
  typeof membershipAccountsListResponseSchema
>;

/** Query phân trang. Cursor rác ⇒ 400 ở service (không để rơi vào Prisma thành P2023/500). */
export const membershipAccountsListQuerySchema = z.object({
  cursor: z.string().max(MEMBERSHIP_ACCOUNTS_MAX_CURSOR_LENGTH).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MEMBERSHIP_ACCOUNTS_MAX_LIMIT)
    .optional(),
});

export type MembershipAccountsListQuery = z.infer<
  typeof membershipAccountsListQuerySchema
>;

/* ===========================================================================
 * US-E4.F2-0414 (@UC9513) — LIỆT KÊ NGƯỜI trong nhóm.
 *
 * GET /v1/groups/:id/people
 *
 * 🔴 VÌ SAO PHẢI LÀ ROUTE RIÊNG, KHÔNG PHẢI MỘT CỜ TRÊN `/members`:
 * `/members` liệt kê GHẾ (thực thể mang nghĩa vụ tiền); route này liệt kê NGƯỜI (thực thể danh
 * tính). Nhét cả hai vào một route rồi rẽ bằng query-param nghĩa là **một danh sách có hai `where`
 * khác nhau** — đúng con bug vừa phải vá ở `groups.service.ts` (US-E4.F1-0411 ⇒ fix 7bcb3dc7:
 * query dựng trang ẩn ghế `self` còn phép đếm thì không ⇒ "còn 2 người nữa" mà bấm xem tiếp chỉ ra
 * 1). Hai thực thể ⇒ hai route.
 *
 * 🔴 NGUỒN: distinct `userId` từ `membership_accounts` của nhóm — MỘT truy vấn đúng cho CẢ HAI loại
 * nhóm, KHÔNG rẽ nhánh theo `group.type`:
 *   · `parent`   ⇒ ra phụ huynh, KỂ CẢ người chưa có ghế riêng (họ chỉ tồn tại dưới dạng account).
 *   · `standard` ⇒ ra chính các user, vì `self` cũng là một account.
 * Rẽ nhánh theo loại nhóm là tự tạo nhánh thứ hai để sau này lệch.
 *
 * 🔴 KHÔNG lọc bỏ người đã là đại diện của suất nào: UI là danh sách có tick, người đã gắn hiển thị
 * ở trạng thái ĐÃ TICK và bỏ tick ⇒ `DELETE .../accounts/:userId`. Trả thiếu người ⇒ UI không dựng
 * được trạng thái ban đầu.
 * ========================================================================= */

/** Trần limit 1 trang. Nhóm lớp học vài chục người; cursor vẫn BẮT BUỘC (quy ước repo mọi list). */
export const GROUP_PEOPLE_MAX_LIMIT = 50;

/** Cursor opaque luôn ngắn; chặn input khổng lồ (DoS) trước khi decode. */
export const GROUP_PEOPLE_MAX_CURSOR_LENGTH = 512;

/** Trần độ dài `q` — chặn tại biên, không để chuỗi khổng lồ đi vào LIKE. */
export const GROUP_PEOPLE_MAX_QUERY_LENGTH = 100;

/**
 * Một NGƯỜI trong nhóm.
 *  - userId    : dùng NGAY cho `POST .../accounts` (gắn) và `DELETE .../accounts/:userId` (gỡ).
 *  - fullName  : `null` khi User chưa đặt tên — KHÔNG bịa chuỗi rỗng.
 *  - avatarUrl : URL ĐÃ resolve (KHÔNG phải key R2 thô — key thô làm client vỡ ảnh lặng lẽ).
 *  - role      : vai THẬT của NGƯỜI ĐÓ; `null` = họ không có ghế riêng. Cùng luật với
 *                `membershipAccountListItemSchema.role` — xem khối doc ở đó, KHÔNG viết luật thứ hai.
 */
export const groupPersonListItemSchema = z
  .object({
    userId: z.string().uuid(),
    fullName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    role: groupRoleSchema.nullable(),
  })
  .strict();

export type GroupPersonListItem = z.infer<typeof groupPersonListItemSchema>;

/** Envelope cursor chuẩn repo. Nhóm chưa có ai ⇒ `data: []` (KHÔNG 404). */
export const groupPeopleListResponseSchema = z
  .object({
    data: z.array(groupPersonListItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
        /**
         * @UC9513 (sheet "Chọn phụ huynh" — US-MOB.F1-0424) — số NGƯỜI còn lại sau trang này, TÔN TRỌNG `q` đang lọc.
         * Sheet "Chọn phụ huynh" render nút "Xem thêm min(remainingCount, limit) người"; thiếu
         * field này client chỉ biết "còn nữa" chứ không biết CÒN BAO NHIÊU.
         * `hasNext=false ⇔ remainingCount=0`.
         * Cùng luật với `memberListResponseSchema.pageInfo.remainingCount` — KHÔNG viết luật thứ
         * hai; khác biệt DUY NHẤT là đơn vị đếm ở đây là NGƯỜI (distinct `userId`), không phải ghế.
         */
        remainingCount: z.number().int().min(0),
      })
      .strict(),
  })
  .strict();

export type GroupPeopleListResponse = z.infer<
  typeof groupPeopleListResponseSchema
>;

/** Query phân trang + tìm theo tên. Cursor rác ⇒ 400 ở service (không để Prisma ném P2023/500). */
export const groupPeopleListQuerySchema = z.object({
  q: z.string().max(GROUP_PEOPLE_MAX_QUERY_LENGTH).optional(),
  cursor: z.string().max(GROUP_PEOPLE_MAX_CURSOR_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(GROUP_PEOPLE_MAX_LIMIT).optional(),
});

export type GroupPeopleListQuery = z.infer<typeof groupPeopleListQuerySchema>;
